import React, { useRef, useEffect, useState } from "react";
import { Bold, Italic, Underline, List, ListOrdered, Link2, Heading1, Heading2, Code2, Eye } from "lucide-react";

const ToolBtn = ({ onClick, active, title, testid, children }) => (
  <button
    type="button"
    title={title}
    data-testid={testid}
    onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors ${
      active ? "bg-emerald-100 text-emerald-700" : "text-slate-500 hover:bg-slate-100 hover:text-slate-800"
    }`}
  >
    {children}
  </button>
);

export default function RichTextEditor({ value, onChange }) {
  const ref = useRef(null);
  const [mode, setMode] = useState("visual"); // visual | html

  useEffect(() => {
    if (mode === "visual" && ref.current && ref.current.innerHTML !== (value || "")) {
      ref.current.innerHTML = value || "";
    }
  }, [mode]);

  const exec = (cmd, arg = null) => {
    document.execCommand(cmd, false, arg);
    if (ref.current) onChange(ref.current.innerHTML);
  };

  const addLink = () => {
    const url = window.prompt("Introdu URL-ul:");
    if (url) exec("createLink", url);
  };

  return (
    <div className="rounded-lg border border-slate-200 overflow-hidden" data-testid="rich-text-editor">
      <div className="flex items-center gap-1 px-2 py-1.5 border-b border-slate-200 bg-slate-50 flex-wrap">
        <ToolBtn testid="rte-bold" title="Bold" onClick={() => exec("bold")}><Bold className="w-4 h-4" /></ToolBtn>
        <ToolBtn testid="rte-italic" title="Italic" onClick={() => exec("italic")}><Italic className="w-4 h-4" /></ToolBtn>
        <ToolBtn testid="rte-underline" title="Subliniat" onClick={() => exec("underline")}><Underline className="w-4 h-4" /></ToolBtn>
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <ToolBtn testid="rte-h1" title="Titlu 1" onClick={() => exec("formatBlock", "<h1>")}><Heading1 className="w-4 h-4" /></ToolBtn>
        <ToolBtn testid="rte-h2" title="Titlu 2" onClick={() => exec("formatBlock", "<h2>")}><Heading2 className="w-4 h-4" /></ToolBtn>
        <ToolBtn testid="rte-ul" title="Lista" onClick={() => exec("insertUnorderedList")}><List className="w-4 h-4" /></ToolBtn>
        <ToolBtn testid="rte-ol" title="Lista numerotata" onClick={() => exec("insertOrderedList")}><ListOrdered className="w-4 h-4" /></ToolBtn>
        <ToolBtn testid="rte-link" title="Link" onClick={addLink}><Link2 className="w-4 h-4" /></ToolBtn>
        <div className="ml-auto">
          <ToolBtn
            testid="rte-toggle-html"
            title={mode === "visual" ? "Vezi HTML" : "Vezi vizual"}
            active={mode === "html"}
            onClick={() => setMode(mode === "visual" ? "html" : "visual")}
          >
            {mode === "visual" ? <Code2 className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </ToolBtn>
        </div>
      </div>
      {mode === "visual" ? (
        <div
          ref={ref}
          contentEditable
          suppressContentEditableWarning
          data-testid="rte-content"
          onInput={(e) => onChange(e.currentTarget.innerHTML)}
          className="min-h-[200px] max-h-[360px] overflow-auto px-4 py-3 text-sm outline-none prose prose-sm max-w-none prose-headings:font-semibold prose-a:text-emerald-600"
        />
      ) : (
        <textarea
          data-testid="rte-html-source"
          rows={10}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-4 py-3 text-sm font-mono outline-none resize-none"
        />
      )}
    </div>
  );
}
